import React from 'react';
import { Box, Divider, Paper, Tooltip, Typography } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { metricSummaryTooltipProps } from '../../constants/metricTooltips';
import { SEARCE_COLORS } from '../../theme';
import OutcomesAndGoalsSection from './OutcomesAndGoalsSection';
import type { Goal, HeroAccentColor, HeroData, RagLevel } from './executiveSummaryTypes';

const RAG_STYLES: Record<RagLevel, { bg: string; border: string; color: string; dot: string }> = {
  green: {
    bg: '#ecfdf5',
    border: '#a7f3d0',
    color: '#15803d',
    dot: SEARCE_COLORS.success,
  },
  amber: {
    bg: '#fff8e1',
    border: '#ffe082',
    color: '#e65100',
    dot: SEARCE_COLORS.warning,
  },
  red: {
    bg: '#fef2f2',
    border: '#fecaca',
    color: '#b91c1c',
    dot: SEARCE_COLORS.error,
  },
};

const KPI_BG: Record<HeroAccentColor, string> = {
  amber: SEARCE_COLORS.warning,
  blue: SEARCE_COLORS.primary,
  green: SEARCE_COLORS.success,
  red: SEARCE_COLORS.error,
};

interface ProjectHeroCardProps {
  hero: HeroData;
  goals: Goal[];
  /** `summary` = title, RAG, status line, and metadata row only (Project Insights style). */
  layout?: 'full' | 'summary';
  /** Outcomes table (embedded in full layout) while outcomes API is in flight */
  outcomesLoading?: boolean;
}

const ProjectHeroCard: React.FC<ProjectHeroCardProps> = ({
  hero,
  goals,
  layout = 'full',
  outcomesLoading,
}) => {
  const ragStyle = RAG_STYLES[hero.rag.level];

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        border: `1px solid ${SEARCE_COLORS.border}`,
        borderRadius: '10px',
        p: '20px 24px',
        boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 0 0 1px rgba(0,0,0,.03)',
        mb: '20px',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns:
            layout === 'summary' ? '1fr' : { xs: '1fr', md: '1fr 1fr' },
          columnGap: '28px',
          rowGap: '24px',
        }}
      >
        {/* LEFT COLUMN */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            <Typography
              component="h2"
              sx={{
                fontSize: '1.375rem',
                fontWeight: 800,
                color: SEARCE_COLORS.primary,
                letterSpacing: '-0.025em',
              }}
            >
              {hero.title}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                px: '12px',
                py: '4px',
                borderRadius: '999px',
                fontSize: '0.75rem',
                fontWeight: 700,
                border: `1.5px solid ${ragStyle.border}`,
                background: ragStyle.bg,
                color: ragStyle.color,
              }}
            >
              <CircleIcon sx={{ fontSize: '0.6rem', color: ragStyle.dot }} />
              {hero.rag.label}
            </Box>
          </Box>

          <Typography
            sx={{ color: SEARCE_COLORS.textSecondary, fontSize: '0.8125rem', mt: '5px' }}
          >
            {hero.tagline}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '6px 10px',
              mt: '7px',
              fontSize: '0.75rem',
              color: SEARCE_COLORS.neutral_500,
            }}
          >
            <Box component="span">
              Updated <Box component="time">{hero.lastUpdated}</Box> · SFDC / Zoho
            </Box>
            <Box component="span" sx={{ color: '#cbd5e1' }}>
              |
            </Box>
            <Box component="span">{hero.client}</Box>
            <Box component="span" sx={{ color: '#cbd5e1' }}>
              |
            </Box>
            <Box component="span">
              {hero.pr}
            </Box>
          </Box>

          {layout === 'full' && <Divider sx={{ my: '20px', borderColor: '#e2e8f0' }} />}

          {/* KPI pills */}
          {layout === 'full' && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {hero.kpis.map((kpi) => {
              const pill = (
                <Box
                  sx={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    borderRadius: '10px',
                    px: '28px',
                    py: '14px',
                    color: '#fff',
                    minWidth: 130,
                    textAlign: 'center',
                    background: KPI_BG[kpi.color],
                    cursor: kpi.description ? 'help' : 'default',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      opacity: 0.9,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {kpi.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.6rem',
                      fontWeight: 800,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {kpi.value}
                  </Typography>
                </Box>
              );

              if (!kpi.description) {
                return <Box key={kpi.label}>{pill}</Box>;
              }

              return (
                <Tooltip
                  key={kpi.label}
                  title={
                    <>
                      <Box component="strong" sx={{ fontWeight: 700 }}>
                        {kpi.label}
                      </Box>
                      {' — '}
                      {kpi.description}
                    </>
                  }
                  {...metricSummaryTooltipProps}
                >
                  <Box sx={{ display: 'inline-flex' }}>{pill}</Box>
                </Tooltip>
              );
            })}
          </Box>
          )}

          {layout === 'full' && hero.dates && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '6px 16px',
                mt: '14px',
                fontSize: '0.8125rem',
                color: SEARCE_COLORS.textSecondary,
              }}
            >
              <Box component="span">
                <Box component="strong" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary }}>
                  Start date
                </Box>
                {' '}
                {hero.dates.startDate}
              </Box>
              <Box component="span" sx={{ color: '#cbd5e1' }}>
                |
              </Box>
              <Box component="span">
                <Box component="strong" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary }}>
                  Est. end date
                </Box>
                {' '}
                {hero.dates.estimatedEndDate}
              </Box>
              <Box component="span" sx={{ color: '#cbd5e1' }}>
                |
              </Box>
              <Box component="span">
                <Box component="strong" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary }}>
                  Actual end date
                </Box>
                {' '}
                {hero.dates.actualEndDate}
              </Box>
            </Box>
          )}
        </Box>

        {/* RIGHT COLUMN: Outcomes & Goals (nested card styling inside hero) */}
        {layout === 'full' && (
          <Box
            sx={{
              pl: { xs: 0, md: '28px' },
              borderLeft: { xs: 'none', md: `2px solid ${SEARCE_COLORS.border}` },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <OutcomesAndGoalsSection goals={goals} embedded loading={outcomesLoading} />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ProjectHeroCard;
